"use client";

import { useTranslation } from "@guru/shared/i18n";
import { cn } from "@guru/shared/utils/cn";
import { Building2, Check, Minus, Rocket, Sprout } from "lucide-react";
import type React from "react";
import { ScrollReveal } from "@/components/public/scroll-reveal";

const columnIcon: Record<
  "lite" | "scale" | "custom",
  React.ComponentType<{ className?: string }>
> = {
  lite: Sprout,
  scale: Rocket,
  custom: Building2,
};

/** Renders a comparison cell: ✅/❌ become tidy icons, "✅ text" keeps the detail. */
function ComparisonCell({ value }: { value: string }) {
  const v = value.trim();

  if (v === "✅") return <Check className="mx-auto h-5 w-5 text-positive" aria-label="Included" />;
  if (v === "❌")
    return <Minus className="mx-auto h-4 w-4 text-muted-foreground/50" aria-label="Not included" />;

  if (v.startsWith("✅")) {
    return (
      <span className="inline-flex items-center justify-center gap-1.5">
        <Check className="h-4 w-4 shrink-0 text-positive" aria-hidden="true" />
        <span>{v.replace(/^✅\s*/u, "")}</span>
      </span>
    );
  }
  if (v.startsWith("❌")) {
    return (
      <span className="inline-flex items-center justify-center gap-1.5 text-muted-foreground">
        <Minus className="h-4 w-4 shrink-0 text-muted-foreground/50" aria-hidden="true" />
        <span>{v.replace(/^❌\s*/u, "")}</span>
      </span>
    );
  }
  return <span>{v}</span>;
}

export function PricingComparisonTable() {
  const { t } = useTranslation();
  const table = t.pricing.comparisonTable;
  // Reuse the localised "Most Popular" copy from the pricing cards.
  const popularLabel = t.homepage.pricing.tiers.find((tier) => tier.id === "scale")?.badge;

  const columns = [
    { id: "lite" as const, label: table.headers.lite, highlighted: false },
    { id: "scale" as const, label: table.headers.scale, highlighted: true },
    { id: "custom" as const, label: table.headers.custom, highlighted: false },
  ];

  return (
    <section className="relative overflow-clip bg-secondary/30 py-20 sm:py-24">
      <div
        className="ledger-grid ledger-grid-fade pointer-events-none absolute inset-0"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal className="mb-10 text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-deep">
            Side by side
          </p>
          <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {table.title}
          </h2>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th
                    scope="col"
                    className="sticky left-0 z-10 bg-card p-4 text-left font-semibold text-foreground"
                  >
                    {table.headers.feature}
                  </th>
                  {columns.map((col) => {
                    const Icon = columnIcon[col.id];
                    return (
                      <th
                        scope="col"
                        key={col.id}
                        className={cn(
                          "min-w-[120px] p-4 text-center align-bottom",
                          col.highlighted && "bg-brand/[0.07]"
                        )}
                      >
                        {col.highlighted && popularLabel && (
                          <span className="mb-2 inline-block rounded-full bg-brand px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-contrast">
                            {popularLabel}
                          </span>
                        )}
                        <span className="flex flex-col items-center gap-1.5">
                          <Icon
                            className={cn(
                              "h-5 w-5",
                              col.highlighted ? "text-brand-deep" : "text-muted-foreground"
                            )}
                          />
                          <span
                            className={cn(
                              "font-semibold",
                              col.highlighted ? "text-brand-deep" : "text-foreground"
                            )}
                          >
                            {col.label}
                          </span>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => (
                  <tr
                    key={row.feature}
                    className="border-b border-border transition-colors last:border-b-0 hover:bg-muted/40"
                  >
                    <th
                      scope="row"
                      className="sticky left-0 z-10 bg-card p-4 text-left font-medium text-foreground"
                    >
                      {row.feature}
                    </th>
                    <td className="p-4 text-center text-muted-foreground">
                      <ComparisonCell value={row.lite} />
                    </td>
                    <td className="bg-brand/[0.05] p-4 text-center font-medium text-foreground">
                      <ComparisonCell value={row.scale} />
                    </td>
                    <td className="p-4 text-center text-muted-foreground">
                      <ComparisonCell value={row.custom} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
